import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getCansInStock } from '../../actions/inventory';
import { 
    Package, 
    Monitor, 
    Calendar, 
    Filter, 
    Search,
    Download,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    FileSpreadsheet,
    Clock,
    RefreshCw,
    SortAsc,
    SortDesc,
    Box,
    Tag,
    Building
} from 'lucide-react';

import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

const CanStock = ({
    loading,
    cansInStock,
    getCansInStock
}) => {
    const initialState = {
        vendingMachineId: '',
        download: false,
        startDate: '',
        endDate: '',
        days: true
    };

    const [formData, setFormData] = useState(initialState);
    const [pages, setPages] = useState(0);
    const [canData, setCanData] = useState([]);
    const [queryString, setQueryString] = useState("");
    const [isDownloading, setIsDownloading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    // First useEffect to fetch data only on component mount
    useEffect(() => {
        document.title = 'Can Stock';
        if(cansInStock.cans === null){
            getCansInStock("page=1&days=true");
        }
    }, []);
    
    // Second useEffect to process data when cansInStock changes
    useEffect(() => {
        const exportToCSV = (apiData) => {
            const ws = XLSX.utils.json_to_sheet(apiData);
            const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
            const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const data = new Blob([excelBuffer], { type: fileType });
            const fileName = `cans_in_stock_${new Date().toISOString().split('T')[0]}`;
            FileSaver.saveAs(data, fileName + fileExtension);
            setFormData({ ...formData, download: false });
            setIsDownloading(false);
        };

        if(cansInStock.cans){
            let mappedCanData = [];
            cansInStock.cans.forEach(can => {
                let canAge = Math.ceil((new Date().getTime() - new Date(can.updatedAt).getTime())/(1000 * 3600 * 24));
                mappedCanData.push({
                    name: can.vendingMachine.name, 
                    vendingMachineId: can.vendingMachine.vendingMachineId,
                    containerId: can.containerId, 
                    brand: can.product.brand, 
                    days: canAge,
                    updatedAt: new Date(can.updatedAt).toString().split("GMT")[0]
                });
            });
            setCanData(mappedCanData);
            
            if(cansInStock.currentPage === 1){
                setPages(cansInStock.pages);
            }
            
            if(formData.download){
                exportToCSV(mappedCanData);
            }
        }
    }, [cansInStock]);

    const { vendingMachineId, download, startDate, endDate, days } = formData;

    const onChange = (e) => {
        if(e.target.name === "days" || e.target.name === "download"){
            setFormData({ ...formData, [e.target.name]: e.target.value === "true" });
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }        
    };

    const onSubmit = e => {
        e.preventDefault();
        const endDateTime = new Date(formData.endDate).getTime();
        const startDateTime = new Date(formData.startDate).getTime();
        
        if(endDateTime < startDateTime){
            alert("End date cannot be earlier than start date");
            return;
        }
        
        if(formData.download) {
            setIsDownloading(true);
        }
        
        let queryString = "page=1&days=" + formData.days + "&download=" + formData.download;
        if(formData.startDate && formData.endDate){
            queryString += "&startDate=" + formData.startDate + "&endDate=" + formData.endDate;
        }
        if(formData.vendingMachineId){
            queryString += "&vendingMachine=" + formData.vendingMachineId;
        }
        setQueryString(queryString);
        getCansInStock(queryString);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? 
                <SortAsc size={14} className="ms-1 text-primary" /> : 
                <SortDesc size={14} className="ms-1 text-primary" />;
        }
        return null;
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig.key) return canData;
        
        return [...canData].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [canData, sortConfig]);

    const onPageChange = (pageNo) => {
        let query = "";
        if(queryString){
            query = queryString.replace("page=" + cansInStock.currentPage, "page=" + pageNo);
        } else {
            query = "page=" + pageNo + "&days=true";
        }
        getCansInStock(query);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getAgeColor = (days) => {
        if (days <= 7) return 'text-success';
        if (days <= 30) return 'text-warning';
        return 'text-danger';
    };

    const getAgeBadge = (days) => {
        if (days <= 7) return 'bg-success';
        if (days <= 30) return 'bg-warning';
        return 'bg-danger';
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <Package size={32} className="me-3 text-primary" />
                                    Cans In Stock
                                </h1>
                                <p className="text-muted mb-0">
                                    Monitor inventory levels and stock age across vending machines
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <div className="d-flex align-items-center text-muted">
                                        <RefreshCw size={16} className="me-2 spinner-border spinner-border-sm" />
                                        Loading...
                                    </div>
                                )}
                                <span className="badge bg-primary fs-6">
                                    {canData.length} Can{canData.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search/Filter Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-light">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Filter size={20} className="me-2 text-primary" />
                                    Filter Inventory
                                </h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={onSubmit}>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-lg-2 col-md-4">
                                            <label className="form-label fw-bold">
                                                <Monitor size={16} className="me-2 text-primary" />
                                                Machine ID
                                            </label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder="Vending Machine ID"
                                                name="vendingMachineId"
                                                value={vendingMachineId}
                                                onChange={onChange}
                                            />
                                        </div>

                                        <div className="col-lg-2 col-md-4">
                                            <label className="form-label fw-bold">
                                                <Calendar size={16} className="me-2 text-success" />
                                                Start Date
                                            </label>
                                            <input
                                                className="form-control"
                                                type="date"
                                                name="startDate"
                                                value={startDate}
                                                onChange={onChange}
                                            />
                                        </div>

                                        <div className="col-lg-2 col-md-4">
                                            <label className="form-label fw-bold">
                                                <Calendar size={16} className="me-2 text-warning" />
                                                End Date
                                            </label>
                                            <input
                                                className="form-control"
                                                type="date"
                                                name="endDate"
                                                value={endDate}
                                                onChange={onChange}
                                            />
                                        </div>

                                        <div className="col-lg-2 col-md-4">
                                            <label className="form-label fw-bold">
                                                <SortAsc size={16} className="me-2 text-info" />
                                                Sort By
                                            </label>
                                            <select
                                                className="form-select"
                                                name="days"
                                                value={days}
                                                onChange={onChange}
                                            >
                                                <option value="true">📅 Oldest First</option>
                                                <option value="false">🆕 Newest First</option>
                                            </select>
                                        </div>

                                        <div className="col-lg-2 col-md-4">
                                            <label className="form-label fw-bold">
                                                <Download size={16} className="me-2 text-success" />
                                                Download
                                            </label>
                                            <select
                                                className="form-select"
                                                name="download"
                                                value={download}
                                                onChange={onChange}
                                            >
                                                <option value="false">❌ No Download</option>
                                                <option value="true">✅ Download Excel</option>
                                            </select>
                                        </div>

                                        <div className="col-lg-2 col-md-4">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary w-100"
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search size={16} className="me-2" />
                                                        Apply Filters
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Can Stock Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Box size={20} className="me-2 text-primary" />
                                    Stock Inventory
                                    {canData.length > 0 && (
                                        <span className="badge bg-info ms-2">{canData.length} items</span>
                                    )}
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                {canData.length > 0 ? (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('name')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Building size={16} className="me-2" />
                                                                VM Name
                                                                {getSortIcon('name')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('vendingMachineId')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Monitor size={16} className="me-2" />
                                                                VM ID
                                                                {getSortIcon('vendingMachineId')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('containerId')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Package size={16} className="me-2" />
                                                                Container ID
                                                                {getSortIcon('containerId')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('brand')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Tag size={16} className="me-2" />
                                                                Product
                                                                {getSortIcon('brand')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('updatedAt')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Calendar size={16} className="me-2" />
                                                                Stocked On
                                                                {getSortIcon('updatedAt')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('days')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Clock size={16} className="me-2" />
                                                                Days Old
                                                                {getSortIcon('days')}
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedData.map((row, index) => (
                                                        <tr key={index} className="align-middle">
                                                            <td className="fw-bold text-primary">
                                                                {row.name}
                                                            </td>
                                                            <td className="text-muted">
                                                                <code className="bg-light px-2 py-1 rounded">
                                                                    {row.vendingMachineId}
                                                                </code>
                                                            </td>
                                                            <td className="fw-bold">
                                                                <span className="badge bg-secondary bg-opacity-10 text-dark px-3 py-2">
                                                                    {row.containerId}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-info bg-opacity-10 text-info px-3 py-2">
                                                                    {row.brand}
                                                                </span>
                                                            </td>
                                                            <td className="text-muted small">
                                                                {formatDate(row.updatedAt)}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${getAgeBadge(row.days)} px-3 py-2`}>
                                                                    {row.days} day{row.days !== 1 ? 's' : ''}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        {pages > 1 && (
                                            <div className="card-footer bg-light">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="text-muted small">
                                                        Showing page {cansInStock.currentPage} of {pages}
                                                    </div>
                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0">
                                                            <li className={`page-item ${cansInStock.currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(1)}
                                                                    disabled={cansInStock.currentPage === 1}
                                                                >
                                                                    <ChevronsLeft size={14} />
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${cansInStock.currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(cansInStock.currentPage - 1)}
                                                                    disabled={cansInStock.currentPage === 1}
                                                                >
                                                                    <ChevronLeft size={14} />
                                                                </button>
                                                            </li>
                                                            
                                                            {/* Page Numbers */}
                                                            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                                                                const startPage = Math.max(1, cansInStock.currentPage - 2);
                                                                const pageNum = startPage + i;
                                                                if (pageNum <= pages) {
                                                                    return (
                                                                        <li key={pageNum} className={`page-item ${cansInStock.currentPage === pageNum ? 'active' : ''}`}>
                                                                            <button 
                                                                                className="page-link"
                                                                                onClick={() => onPageChange(pageNum)}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                            
                                                            <li className={`page-item ${cansInStock.currentPage === pages ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(cansInStock.currentPage + 1)}
                                                                    disabled={cansInStock.currentPage === pages}
                                                                >
                                                                    <ChevronRight size={14} />
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${cansInStock.currentPage === pages ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(pages)}
                                                                    disabled={cansInStock.currentPage === pages}
                                                                >
                                                                    <ChevronsRight size={14} />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-5">
                                        <Package size={64} className="text-muted mb-3" />
                                        <h5 className="text-muted">No Stock Data Found</h5>
                                        <p className="text-muted mb-0">
                                            Try adjusting your filters or check back later for inventory updates.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

CanStock.propTypes = {
    getCansInStock: PropTypes.func.isRequired,
    cansInStock: PropTypes.object,
    loading: PropTypes.bool
};

const mapStateToProps = state => ({
    cansInStock: state.inventory.cansInStock,
    loading: state.inventory.loading,
});

export default connect(mapStateToProps, { getCansInStock })(CanStock);